import React, { useState, useEffect } from "react";
import {
  DndContext,
  MouseSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import SortableItem from "../components/SortableItem"; // Import SortableItem component
import Slide from "../components/Slide";
import VerbatimList from "../components/VerbatimList";
import { useVerbatim } from "../context/VerbatimContext";
import { useSetup } from "../context/SetupContext"; // Import the Setup context
import VerbatimFilter from "../components/VerbatimFilter"; // Import the filter function
import MinimalistButton from "../components/buttons/MinimalistButton";
import DeleteButton from "../components/buttons/DeleteButton";
import PrimaryButton from "../components/buttons/PrimaryButton";
import SecondaryButton from "../components/buttons/SecondaryButton";
import {handleGeneratePpt} from "../utils/PPTUtils";
import { removeFromIndexedDB } from '../utils/IndexedDBUtils';

import FilterBar from "../components/FilterBar";

import "./InsightPage.css";

const InsightPage = () => {
  const { getVerbatims, notes, setNotes, slides, setSlides } = useVerbatim();
  const { labels, labelTypes } = useSetup(); // Use the labels from the Setup context
  const [activeSlide, setActiveSlide] = useState(
    slides.length > 0 ? slides[0].id : null
  );
  const [activeTab, setActiveTab] = useState("verbatimList");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("");
  const [filteredVerbatims, setFilteredVerbatims] = useState([]); // Filtered verbatims to display
  const [isCollapsed, setIsCollapsed] = useState(true); // Track collapsed/expanded state
  const [filters, setFilters] = useState({
    source: "",
    dynamicFilters: Object.keys(labelTypes).reduce((acc, labelType) => {
      acc[labelType] = [];
      return acc;
    }, {}),
  }); // Initialize filters state

  const togglePanel = () => {
    setIsCollapsed(!isCollapsed); // Toggle between collapsed and expanded states
  };

  // Apply filters whenever verbatims or filters change
  useEffect(() => {
    const verbatims = getVerbatims(); // Get verbatims from VerbatimContext

    if (Array.isArray(verbatims) && verbatims.length > 0) {
      // Sort verbatims in descending order by ID, handling potential null/undefined IDs
      const sortedVerbatims = [...verbatims].sort(
        (a, b) => (b.id ?? 0) - (a.id ?? 0)
      );

      // Check that filters is valid before applying VerbatimFilter
      const filtered = filters
        ? VerbatimFilter(sortedVerbatims, filters)
        : sortedVerbatims;

      setFilteredVerbatims(filtered); // Update filtered verbatims state
      console.log(
        "VerbatimFilter applied. Filtered verbatims updated:",
        filtered
      );
    } else {
      setFilteredVerbatims([]); // Ensure filteredVerbatims is an empty array if verbatims is invalid
      console.log(
        "No verbatims to filter. Filtered verbatims set to an empty array."
      );
    }
  }, [getVerbatims, filters]); // Re-run whenever getVerbatims or filters change

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
    },
  });

  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });
  const sensors = useSensors(mouseSensor, keyboardSensor);

  const handleDragEnd = (event) => {
    const note = notes.find((x) => x.id === event.active.id);
    if (note) {
      note.position.x += event.delta.x;
      note.position.y += event.delta.y;
      const updatedNotes = notes.map((x) => (x.id === note.id ? note : x));
      setNotes(updatedNotes);
    }

    const { active, over } = event;

    if (active.id !== over.id) {
      setSlides((slides) => {
        const oldIndex = slides.findIndex((slide) => slide.id === active.id);
        const newIndex = slides.findIndex((slide) => slide.id === over.id);

        return arrayMove(slides, oldIndex, newIndex);
      });
    }
  };

  const handleAddVerbatim = (verbatimObj) => {
    const newNote = {
      id: `${notes.length + 1}`,
      content: verbatimObj.verbatim,
      link: verbatimObj.link || "", // Add the link property, or an empty string if no link is provided
      position: {
        x: 0,
        y: 0,
      },
      slideId: activeSlide,
    };
    setNotes([...notes, newNote]);
  };

  const handleDeleteNote = (id) => {
    // Ensure notes is an array, otherwise use an empty array
    const updatedNotes = Array.isArray(notes) ? notes.filter((note) => note.id !== id) : [];
    setNotes(updatedNotes);
  };
  
  const handleAddSlide = () => {
    const newSlide = {
      id: slides.length > 0 ? slides[slides.length - 1].id + 1 : 1,
      title: `Slide ${slides.length + 1}`,
    };
    setSlides([...slides, newSlide]);
    setActiveSlide(newSlide.id);
  };

  const handleDeleteSlide = (slideId) => {
    const updatedSlides = slides.filter((slide) => slide.id !== slideId);
    setSlides(updatedSlides);
    const updatedNotes = notes.filter((note) => note.slideId !== slideId);
    setNotes(updatedNotes);
    if (activeSlide === slideId) {
      setActiveSlide(updatedSlides.length > 0 ? updatedSlides[0].id : null);
    }
  };

  const handleUpdateSlideTitle = (slideId, newTitle) => {
    const updatedSlides = slides.map((slide) =>
      slide.id === slideId ? { ...slide, title: newTitle } : slide
    );
    setSlides(updatedSlides);
  };

  const handleDeleteAllNotes = () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete all notes?"
    );
    if (confirmDelete) {
      setNotes([]);
      removeFromIndexedDB('notes');
    }
  };

  const handleDeleteAllSlides = () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete all slides and notes?"
    );
    if (confirmDelete) {
      setSlides([]);
      setNotes([]);
      setActiveSlide(null);
      removeFromIndexedDB('notes');
      removeFromIndexedDB('slides');
    }
  };

  const handleExportConfiguration = () => {
    const data = {
      slides,
      notes,
    };

    const json = JSON.stringify(data);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "slide-configuration.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLoadConfiguration = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = JSON.parse(e.target.result);
        if (data.slides && data.notes) {
          setSlides(data.slides);
          setNotes(data.notes);
          setActiveSlide(data.slides.length > 0 ? data.slides[0].id : null);
        }
      };
      reader.readAsText(file);
    }
  };

  const fileInputRef = React.createRef();

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleFilterChange = (newFilters) => {
    console.log("Filters updated:", newFilters);
    setFilters(newFilters); // Update the filters state
  };

  
  const currentNotes = notes.filter((note) => note.slideId === activeSlide);

  const activeSlideData = slides.find((slide) => slide.id === activeSlide);

  const callGeneratePPT = () => {
    handleGeneratePpt(notes,slides);
  }

  return (
    <div className="insight-page">
      <div
        className={`left-panel ${isCollapsed ? "collapsed" : "expanded"}`}
        onClick={isCollapsed ? togglePanel : undefined} // Only trigger when collapsed
      >
        {/* Only show this button in collapsed state */}
        {isCollapsed ? (
          <div className="toggle-button">
            <i className="fa-solid fa-filter"></i>{" "}
            {/* Icon indicating it's expandable */}
          </div>
        ) : (
          <div className="filter-bar-container">
            {/* FilterBar appears when the panel is expanded */}
            <button className="collapse-button" onClick={togglePanel}>
              <i className="fa-solid fa-xmark"></i> {/* Collapse icon */}
            </button>
            <FilterBar onFilterChange={handleFilterChange} />
          </div>
        )}
      </div>
      <div className="middle-panel">
        <div className="tabs">
          <button
            onClick={() => setActiveTab("verbatimList")}
            className={activeTab === "verbatimList" ? "active" : ""}
          >
            Verbatims
          </button>
          <button
            onClick={() => setActiveTab("slideList")}
            className={activeTab === "slideList" ? "active" : ""}
          >
            Slides
          </button>
          <button
            onClick={() => setActiveTab("settings")}
            className={activeTab === "settings" ? "active" : ""}
          >
            Settings
          </button>
        </div>

        {activeTab === "verbatimList" && (
          <VerbatimList
            verbatims={filteredVerbatims}
            notes={notes}
            slides={slides}
            onAdd={handleAddVerbatim}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedLabel={selectedLabel}
            setSelectedLabel={setSelectedLabel}
            labels={Object.keys(labels)} // Pass the array of label strings
            labelTypes={labelTypes}
            activeSlide={activeSlide} // Pass activeSlide
          />
        )}
        {activeTab === "slideList" && (
          <div className="slide-list">
            <DndContext
              sensors={sensors}
              onDragEnd={handleDragEnd}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={slides}
                strategy={verticalListSortingStrategy}
              >
                {slides.map((slide) => (
                  <SortableItem
                    key={slide.id}
                    id={slide.id}
                    slide={slide}
                    activeSlide={activeSlide}
                    setActiveSlide={setActiveSlide}
                    handleDeleteSlide={handleDeleteSlide}
                  />
                ))}
              </SortableContext>
            </DndContext>
            <div className="add-button-container">
              <MinimalistButton onClick={handleAddSlide}>+</MinimalistButton>
            </div>
          </div>
        )}
        {activeTab === "settings" && (
          <div className="settings">
            <PrimaryButton onClick={handleExportConfiguration}>
              <i className="fa-solid fa-download"></i>Export Slide Configuration
            </PrimaryButton>
            <SecondaryButton onClick={triggerFileInput}>
              <i className="fa-solid fa-upload"></i>Load Slide Configuration
            </SecondaryButton>
            <SecondaryButton onClick={callGeneratePPT}>
              <i className="fas fa-file-powerpoint"></i>Generate PPT
            </SecondaryButton>
            <DeleteButton onClick={handleDeleteAllNotes}>
              <i className="fa-solid fa-trash"></i> Delete All Notes
            </DeleteButton>
            <DeleteButton onClick={handleDeleteAllSlides}>
              <i className="fa-solid fa-trash"></i> Delete All Slides
            </DeleteButton>
            <input
              type="file"
              accept=".json"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleLoadConfiguration}
            />
          </div>
        )}
      </div>
      <div className="right-panel">
        {activeSlide && activeSlideData && (
          <DndContext
            sensors={sensors}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToParentElement]}
          >
            <Slide
              slide={activeSlideData}
              notes={currentNotes}
              onDeleteNote={handleDeleteNote}
              onUpdateTitle={handleUpdateSlideTitle}
            />
          </DndContext>
        )}
      </div>
    </div>
  );
};

export default InsightPage;
